import React, { useEffect, useState } from "react";
import { Activity, Filter, Sparkles, Bookmark, ArrowRight, X } from "lucide-react";

const KEY = "ff_onboarded_v1";

const SLIDES = [
  {
    eyebrow: "Welcome",
    title: "Trade with intent.",
    subtitle: "FlowFinder hunts clean bullish & bearish setups across stocks, ETFs, futures and micro futures — all on one quiet, premium terminal.",
    icon: Activity,
    accent: "var(--bull)",
    visual: "logo",
  },
  {
    eyebrow: "Filter builder",
    title: "Build any scan in seconds.",
    subtitle: "Pick a market, asset type, timeframe and indicators. We do the math — RSI, MACD, VWAP, Bollinger, RVOL and EMA 9/20/50 — every refresh.",
    icon: Filter,
    accent: "#6c5ce7",
    visual: "filters",
  },
  {
    eyebrow: "Signal strength",
    title: "1 to 100, ranked live.",
    subtitle: "Every match is scored from 1–100 based on how many of your filters align — RSI, MACD, EMA, VWAP, Bollinger, RVOL. Green is bullish, red is bearish, and the radar dot pulses harder when conviction is strong.",
    icon: Sparkles,
    accent: "var(--bull)",
    visual: "strength",
  },
  {
    eyebrow: "Save your edge",
    title: "Custom scans, one click away.",
    subtitle: "Save your favorite setups — MNQ Scalper, VWAP Bounce Finder, High RSI Reversal — and reload them instantly from the My Scans panel.",
    icon: Bookmark,
    accent: "var(--bear)",
    visual: "saved",
  },
];

export default function Onboarding({ force = false }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (force) { setOpen(true); return; }
    try { if (!localStorage.getItem(KEY)) setOpen(true); } catch (e) {}
  }, [force]);

  const close = () => {
    try { localStorage.setItem(KEY, "1"); } catch (e) {}
    setOpen(false);
    setStep(0);
  };

  if (!open) return null;

  const isLast = step === SLIDES.length - 1;
  const s = SLIDES[step];

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-2xl flex items-center justify-center p-4 fade-in" data-testid="onboarding">
      <button onClick={close} className="absolute top-5 right-5 text-white/50 hover:text-white p-2" data-testid="onboarding-close">
        <X className="w-5 h-5" />
      </button>
      <div className="relative w-full max-w-2xl">
        <div
          className="absolute -inset-12 pointer-events-none opacity-60 transition-all duration-700"
          style={{ background: `radial-gradient(600px 400px at 50% 30%, ${s.accent}33, transparent 70%)`, filter: "blur(60px)" }}
        />
        <div className="relative glass-strong rounded-3xl p-8 sm:p-12 overflow-hidden">
          {/* progress */}
          <div className="flex items-center gap-1.5 mb-8">
            {SLIDES.map((_, i) => (
              <div key={i} className="h-[3px] flex-1 rounded-full overflow-hidden bg-white/10">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: i < step ? "100%" : i === step ? "100%" : "0%", background: i <= step ? "rgba(255,255,255,0.9)" : "transparent" }}
                />
              </div>
            ))}
          </div>

          <div key={step} className="fade-in">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center" style={{ boxShadow: `0 0 24px ${s.accent}33` }}>
                <s.icon className="w-4 h-4 text-white" />
              </div>
              <div className="label-mono">{s.eyebrow}</div>
            </div>
            <h2 className="text-3xl sm:text-5xl text-white font-semibold tracking-[-0.025em] leading-[1.05]">{s.title}</h2>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed mt-4 max-w-xl">{s.subtitle}</p>

            <div className="mt-8 rounded-2xl border border-white/5 bg-white/[0.02] p-5 min-h-[140px]">
              <Visual kind={s.visual} accent={s.accent} />
            </div>
          </div>

          <div className="flex items-center justify-between mt-8">
            <button onClick={close} className="text-white/40 text-sm hover:text-white tracking-tight" data-testid="onboarding-skip">Skip tour</button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="onboarding-back">Back</button>
              )}
              <button onClick={() => isLast ? close() : setStep(step + 1)} className="btn-primary flex items-center gap-1.5" data-testid="onboarding-next">
                {isLast ? "Get started" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Visual({ kind, accent }) {
  if (kind === "logo") {
    return (
      <div className="flex items-center justify-center py-2">
        <div className="relative">
          <div className="absolute -inset-6 rounded-full" style={{ background: `radial-gradient(circle, ${accent}30, transparent 70%)`, filter: "blur(20px)" }} />
          <img src="/logo192.png" alt="" className="relative w-20 h-20 rounded-2xl border border-white/10" />
        </div>
      </div>
    );
  }
  if (kind === "filters") {
    return (
      <div className="flex flex-wrap gap-1.5">
        {["Stocks", "15m", "Low RSI", "MACD bull", "VWAP bounce", "High RVOL", "Bullish"].map((t, i) => (
          <span key={t} className={`chip ${i === 0 || i === 1 ? "active" : i >= 2 && i < 6 ? "bull active" : ""}`}>{t}</span>
        ))}
      </div>
    );
  }
  if (kind === "strength") {
    return (
      <div className="space-y-2">
        {[{ t: "NVDA", s: 87, d: "bull" }, { t: "TSLA", s: 76, d: "bear" }, { t: "ES", s: 64, d: "bull" }].map((r, i) => (
          <div key={r.t} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="relative inline-block w-2 h-2 rounded-full" style={{ background: r.d === "bull" ? "var(--bull)" : "var(--bear)", boxShadow: `0 0 10px ${r.d === "bull" ? "var(--bull)" : "var(--bear)"}` }} />
              <span className="text-white text-sm font-semibold tracking-tight">{r.t}</span>
            </div>
            <div className="flex items-center gap-2 flex-1 max-w-[260px]">
              <div className="h-1 flex-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${r.s}%`, background: r.d === "bull" ? "var(--bull)" : "var(--bear)", boxShadow: `0 0 10px ${r.d === "bull" ? "var(--bull-glow)" : "var(--bear-glow)"}`, animation: `grow-${i} 1s ease forwards` }}
                />
              </div>
              <span className="font-mono text-white text-xs tabular-nums w-7 text-right">{r.s}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "saved") {
    return (
      <div className="grid sm:grid-cols-3 gap-2">
        {[{ e: "↩️", n: "High RSI Reversal" }, { e: "🎯", n: "VWAP Bounce Finder" }, { e: "⚡", n: "MNQ Scalper" }].map((p) => (
          <div key={p.n} className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10">
            <div className="text-lg">{p.e}</div>
            <div className="text-white text-[12px] font-medium tracking-tight mt-1">{p.n}</div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}
